import { useRef, useState } from 'react';
import { Truck, Upload, Loader2, Lock, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import CampoNumero from './CampoNumero';
import { formatarMoeda, formatarCnpj } from './formato';

/**
 * Frete FOB: fica aberto para digitação (o XML da NF-e não traz o valor pago à transportadora).
 * Importar o XML do CT-e é opcional e preenche os campos. Em CIF fica bloqueado: o frete já está na nota.
 */
export default function PainelFrete({ nota, frete, onFrete }) {
  const inputRef = useRef(null);
  const [importando, setImportando] = useState(false);
  const aberto = nota.freteFicaComALoja || frete.liberadoManual;

  const alterar = (chave, valor) => onFrete({ ...frete, [chave]: valor });

  const alterarComponente = (indice, chave, valor) => onFrete({
    ...frete,
    componentes: frete.componentes.map((c, i) => (i === indice ? { ...c, [chave]: valor } : c)),
  });

  const somaComponentes = frete.componentes.reduce((s, c) => s + (Number(c.valor) || 0), 0);

  async function importarCTe(arquivo) {
    if (!arquivo) return;
    setImportando(true);
    try {
      const resp = await apiService.precificacao.importarCTe(arquivo, {
        chaveNFe: nota.chave,
        cnpjLoja: nota.destinatario.cnpj,
      });
      const { cte, avisos } = resp.data.data;
      onFrete({
        ...frete,
        valorTotal: cte.valorTotal,
        icmsCTe: cte.icms.vICMS,
        componentes: cte.componentes,
        percentualNota: cte.chavesNFe.length > 1 ? frete.percentualNota : 100,
        cte,
        avisos,
      });
      toast.success(`CT-e ${cte.numero} importado`);
    } catch (error) {
      console.error('❌ Erro ao importar CT-e:', error);
    } finally {
      setImportando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  if (!aberto) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-wrap items-center gap-3">
        <Lock className="w-5 h-5 text-green-600" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">Frete CIF — já incluído na nota</p>
          <p className="text-sm text-gray-500">O fornecedor pagou o frete e o valor já está nos produtos. Não há CT-e para lançar.</p>
        </div>
        <button
          type="button"
          onClick={() => alterar('liberadoManual', true)}
          className="text-xs text-blue-600 hover:underline"
        >
          Houve frete extra mesmo assim? Lançar
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-orange-200 rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-orange-600" />
          <div>
            <p className="font-semibold text-gray-900">Frete pago pela loja (CT-e)</p>
            <p className="text-sm text-gray-500">Informe quanto foi pago à transportadora ou importe o XML do CT-e.</p>
          </div>
        </div>
        <div>
          <input ref={inputRef} type="file" accept=".xml" className="hidden" onChange={e => importarCTe(e.target.files?.[0])} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={importando}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 disabled:opacity-60"
          >
            {importando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importar XML do CT-e (opcional)
          </button>
        </div>
      </div>

      {frete.cte && (
        <p className="text-xs text-gray-600">
          CT-e {frete.cte.numero} · {frete.cte.emitente.nome} · tomador: {frete.cte.tomador.nome || formatarCnpj(frete.cte.tomador.cnpj)}
          {' · '}{frete.cte.chavesNFe.length} NF-e vinculada(s)
        </p>
      )}
      {frete.avisos?.length > 0 && (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5">
          {frete.avisos.map(a => (
            <li key={a} className="flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{a}</li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CampoNumero label="Valor total do frete (vTPrest) R$" valor={frete.valorTotal} onChange={v => alterar('valorTotal', v)} minimo={0} destaque />
        <CampoNumero
          label="ICMS do CT-e R$"
          valor={frete.icmsCTe}
          onChange={v => alterar('icmsCTe', v)}
          minimo={0}
          titulo="Gera crédito no Lucro Presumido (itens sem ST)"
        />
        <CampoNumero
          label="% do frete que é desta nota"
          valor={frete.percentualNota}
          onChange={v => alterar('percentualNota', v)}
          minimo={0}
          titulo="Use quando um CT-e cobre várias notas (ex.: proporcional ao peso)"
        />
        <label className="block">
          <span className="block text-[11px] font-medium text-gray-500 mb-0.5">Dividir entre os itens por</span>
          <select
            value={frete.rateio}
            onChange={e => alterar('rateio', e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
          >
            <option value="valor">Valor do item</option>
            <option value="quantidade">Quantidade de peças</option>
          </select>
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-gray-700">
            Componentes do frete (frete-peso, ad valorem, GRIS, pedágio, TDE, taxas) — só para conferência
          </p>
          <button
            type="button"
            onClick={() => alterar('componentes', [...frete.componentes, { nome: '', valor: 0 }])}
            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <Plus className="w-3.5 h-3.5" /> Componente
          </button>
        </div>
        {frete.componentes.length === 0 ? (
          <p className="text-xs text-gray-400">Nenhum componente informado.</p>
        ) : (
          <div className="space-y-1">
            {frete.componentes.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={c.nome}
                  onChange={e => alterarComponente(i, 'nome', e.target.value)}
                  placeholder="Nome (ex.: GRIS)"
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md"
                />
                <input
                  type="number"
                  step="any"
                  min={0}
                  value={c.valor}
                  onChange={e => alterarComponente(i, 'valor', e.target.value)}
                  className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => alterar('componentes', frete.componentes.filter((_, j) => j !== i))}
                  className="p-1 text-gray-400 hover:text-red-600"
                  aria-label="Remover componente"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <p className={`text-xs ${Math.abs(somaComponentes - Number(frete.valorTotal || 0)) > 0.01 ? 'text-red-600' : 'text-gray-500'}`}>
              Soma dos componentes: {formatarMoeda(somaComponentes)} (total informado: {formatarMoeda(Number(frete.valorTotal || 0))})
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
